import React, { useState } from 'react';
// Import Lucide React icons for the services
import { Car, Wrench, BatteryCharging, ShieldCheck, Gauge, Lightbulb, ChevronDown } from 'lucide-react';

const Services = () => {
    // State to manage which accordion item is currently open
    // Stores the index of the open item. Null if none are open.
    const [activeIndex, setActiveIndex] = useState(null);

    // Function to toggle an accordion item
    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    // Define your garage services here
    const services = [
        {
            title: "Entretien & Révision",
            description: "Maintenance complète de votre véhicule, vidanges, filtres, niveaux, et diagnostics pour une longévité optimale. Nous suivons rigoureusement les préconisations constructeurs.",
            icon: Car
        },
        {
            title: "Réparations Mécaniques",
            description: "Diagnostic et réparation de tous problèmes mécaniques : freinage, suspension, direction, et systèmes d'échappement. Nous utilisons des pièces certifiées.",
            icon: Wrench,
            image: "/images/mecanique_freins.png"
        },
        {
            title: "Dépannage 24h/24",
            description: "Service de dépannage rapide et efficace, disponible 24h/24, 7j/7, pour toute urgence sur route ou à domicile dans la région de Lahage.",
            icon: BatteryCharging
        },
        {
            title: "Pré-Contrôle Technique",
            description: "Inspection complète et préparation de votre véhicule pour le contrôle technique, avec les ajustements nécessaires pour assurer la conformité d'office.",
            icon: ShieldCheck
        },
        {
            title: "Diagnostic Électronique",
            description: "Analyse approfondie avec nos valises multimarques pour identifier les pannes électroniques (ABS, ESP, airbags, voyants moteur).",
            icon: Gauge,
            image: "/images/mecanique_diag.png"
        },
        {
            title: "Climatisation & Chauffage",
            description: "Recharge, entretien et détection de fuites de votre système de climatisation pour un confort optimal en toutes saisons.",
            icon: Lightbulb
        }
    ];

    return (
        <section id="services" className="services-section">
            <div className="services-container">
                <h2 className="services-title">Nos Services</h2>
                <div className="accordion-grid">
                    {services.map((service, index) => (
                        <div key={index} className="accordion-item">
                            <button
                                className={`accordion-header ${activeIndex === index ? 'active' : ''}`}
                                onClick={() => toggleAccordion(index)}
                                aria-expanded={activeIndex === index ? "true" : "false"}
                            >
                                <div className="header-content">
                                    <span className="accordion-icon">
                                        {React.createElement(service.icon, { size: 30, color: "#1A365D" })}
                                    </span>
                                    <h3 className="accordion-title">{service.title}</h3>
                                </div>
                                <ChevronDown
                                    size={24}
                                    className={`arrow-icon ${activeIndex === index ? 'rotate' : ''}`}
                                    color="#1A365D"
                                />
                            </button>
                            <div
                                className={`accordion-collapse ${activeIndex === index ? 'show' : ''}`}
                                style={{
                                    maxHeight: activeIndex === index ? '500px' : '0', // Increased to accommodate images
                                    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                                }}
                            >
                                <div className="accordion-body">
                                    <div className="service-body-content">
                                        <div className="service-text-col">
                                            <p className="service-description">{service.description}</p>
                                            <ul className="service-bullets">
                                                <li>Qualité d'origine garantie</li>
                                                <li>Diagnostic approfondi inclus</li>
                                            </ul>
                                        </div>
                                        {service.image && (
                                            <div className="service-image-col">
                                                <img src={service.image} alt={service.title} className="service-img" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <style jsx>{`
                .services-section {
                    padding: 60px 20px;
                    background-color: #f0f4f8; /* Light gray background */
                    text-align: center;
                }

                .services-container {
                    max-width: 900px; /* Slightly narrower for accordion for better readability */
                    margin: 0 auto;
                }

                .services-title {
                    font-size: 2.5rem;
                    margin-bottom: 50px;
                    color: #1A365D; /* Dark blue title */
                    text-transform: uppercase;
                    font-weight: bold;
                }

                .accordion-grid {
                    display: flex;
                    flex-direction: column;
                    gap: 15px; /* Space between accordion items */
                }

                .accordion-item {
                    background-color: #ffffff;
                    border-radius: 12px;
                    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
                    overflow: hidden; /* Ensures content is clipped when collapsed */
                    transition: box-shadow 0.3s ease;
                }

                .accordion-item:hover {
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
                }

                .accordion-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                    padding: 20px 25px;
                    background-color: #ffffff;
                    border: none;
                    cursor: pointer;
                    text-align: left;
                    font-family: inherit; /* Ensure font consistency */
                    color: #1A365D;
                    font-size: 1.25rem;
                    font-weight: 600;
                    transition: background-color 0.3s ease, color 0.3s ease;
                }

                .accordion-header:hover {
                    background-color: #f5f5f5;
                }

                .accordion-header.active {
                    background-color: #e0e9f3; /* Lighter blue when active */
                }

                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }

                .accordion-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background-color: #cce7ff; /* Very light blue circle for icon */
                }

                .accordion-title {
                    margin: 0;
                    font-size: 1.3rem;
                    color: inherit; /* Inherits from accordion-header */
                }

                .arrow-icon {
                    transition: transform 0.3s ease-in-out;
                }

                .arrow-icon.rotate {
                    transform: rotate(180deg);
                }

                .accordion-collapse {
                    overflow: hidden;
                    transition: max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .accordion-collapse.show {
                    max-height: 500px;
                }

                .accordion-body {
                    padding: 0 25px 25px;
                    text-align: left;
                }

                .service-body-content {
                    display: flex;
                    gap: 30px;
                    align-items: flex-start;
                }

                .service-text-col {
                    flex: 1.2;
                }

                .service-image-col {
                    flex: 0.8;
                }

                .service-description {
                    color: #475569;
                    font-size: 1.1rem;
                    line-height: 1.7;
                    margin-bottom: 20px;
                }

                .service-bullets {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .service-bullets li {
                    position: relative;
                    padding-left: 25px;
                    margin-bottom: 10px;
                    color: #1A365D;
                    font-weight: 500;
                    font-size: 0.95rem;
                }

                .service-bullets li::before {
                    content: '✓';
                    position: absolute;
                    left: 0;
                    color: #10b981;
                    font-weight: bold;
                }

                .service-img {
                    width: 100%;
                    border-radius: 12px;
                    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
                    object-fit: cover;
                    height: 200px;
                    border: 1px solid #e2e8f0;
                }
                
                /* Media Queries */
                @media (max-width: 768px) {
                    .service-body-content {
                        flex-direction: column;
                        gap: 20px;
                    }
                    .service-img {
                        height: 180px;
                    }
                    .accordion-collapse.show {
                        max-height: 700px; /* More space for stacked content */
                    }
                
                /* Media Queries */
                @media (max-width: 768px) {
                    .services-title {
                        font-size: 2.2rem;
                        margin-bottom: 40px;
                    }
                    .accordion-header {
                        padding: 18px 20px;
                        font-size: 1.15rem;
                    }
                    .accordion-title {
                        font-size: 1.2rem;
                    }
                    .accordion-icon {
                        width: 35px;
                        height: 35px;
                    }
                    .accordion-body {
                        font-size: 1rem;
                        padding: 0 20px 15px;
                    }
                }

                @media (max-width: 480px) {
                    .services-title {
                        font-size: 1.9rem;
                        margin-bottom: 30px;
                    }
                    .services-section {
                        padding: 40px 15px;
                    }
                    .accordion-header {
                        padding: 15px;
                        font-size: 1.1rem;
                    }
                    .accordion-title {
                        font-size: 1.1rem;
                    }
                    .accordion-icon {
                        width: 30px;
                        height: 30px;
                    }
                    .accordion-body {
                        font-size: 0.95rem;
                        padding: 0 15px 12px;
                    }
                }
            `}</style>
        </section>
    );
};

export default Services;
